import {
    EventType,
    InterfaceVariablesType,
    PilotagePlanSettingType,
    PlanType,
    PortShipType,
    PositionType,
    SwitchType,
    TrackType,
    TransitOptionType,
    TransitsDataType,
    TransitType,
    VesselType,
} from 'types';
import { FilterObjectType } from 'types/search';
import { ToolTipType } from './../types/index.d';

import { MOVING_VESSEL_THRESHOLD } from 'data/static';
import { dateTimeFormat, DateTimeFormat } from './dateTimeFormat';
import { clone, getId, setKey } from './formHandlers';

export function formatFilterObject(filterObject: FilterObjectType) {
    const apiVersion: FilterObjectType = clone(filterObject, true);

    if (apiVersion.date.end_utc.op === null) {
        setKey(apiVersion, 'date.end_utc', {});
    }
    if (apiVersion.date.start_utc.op === null) {
        setKey(apiVersion, 'date.start_utc', {});
    }
    setKey(apiVersion, 'text', filterObject.withComments ? ['with_user_comments'] : []);

    return apiVersion;
}

export function flatTrackDate(dateTimeFormat: DateTimeFormat, data): PositionType[] {
    return Object.keys(data).map((x: string) => {
        return {
            ...data[x],
            valid_time_utc: dateTimeFormat.parseDateTime(data[x].valid_time_utc),
        };
    });
}

export function updatePilotagePlanSettings(
    pilotagePlanSettings: PilotagePlanSettingType,
    plans: PlanType[]
): PilotagePlanSettingType {
    const updatedSettings: PilotagePlanSettingType = {};

    Object.keys(pilotagePlanSettings).forEach((settingKey) => {
        const setting = pilotagePlanSettings[settingKey];
        const { options } = setting;

        // Check if any of the options has a non-empty value in any plan
        const hasDataInPlans = options?.some((option) =>
            plans.some((plan) => {
                const value = plan[option as keyof PlanType];
                if (Array.isArray(value)) {
                    return value.length > 0 && value.some((v) => v !== '');
                }
                return value !== undefined && value !== null && value !== '';
            })
        );

        if (hasDataInPlans) {
            updatedSettings[settingKey] = setting;
        }
    });
    return updatedSettings;
}

export function isVesselMoving(data) {
    if (data) {
        const sogValues = Object.values(data)
            //@ts-ignore
            .map((v) => v.sog_mps)
            .filter((v) => typeof v === 'number');

        const averageSog = sogValues.reduce((sum, v) => sum + v, 0) / sogValues.length;

        if (averageSog >= MOVING_VESSEL_THRESHOLD) {
            return true;
        }
    }
    return false;
}

export function copyTracksOptions(tracks: TrackType[], options: any[]): TrackType[] {
    return tracks.map((track) => {
        const option = options.find((opt) => opt.name === track.options.group);

        if (!option || option.displayMoving === undefined || option.drawTrackNotMoving === undefined) return track;

        const isMoving = track.options.moving;

        return {
            ...track,
            options: {
                ...track.options,
                display: isMoving ? option.displayMoving : option.displayNotMoving,
            },
        };
    });
}

export function formatTracks(
    transitId: number | null,
    mmsi: number,
    sourceTracks,
    switchs: SwitchType,
    interfaceVariables: InterfaceVariablesType,
    minimumLength: number,
    portShips?: PortShipType[],
    transitShips?: PortShipType[],
    mode = 'search'
): TrackType[] {
    const tracks: TrackType[] = [];

    const getVesselGroup = (
        mmsi: number,
        vessel: VesselType,
        interfaceVariables: InterfaceVariablesType,
        minimumLength: number,
        portShips?: PortShipType[],
        transitShips?: PortShipType[],
        data?: any
    ) => {
        //small vessel
        const moving = isVesselMoving(data);

        if (mmsi === vessel.mmsi) {
            return { group: 'transitVessels', moving, transitId: transitId, status: true, drawTrack: true }; // this is the selected transit
        }
        if (
            portShips?.length &&
            portShips.find((x) => {
                return x.mmsi === vessel.mmsi;
            })
        ) {
            return {
                group: 'portVessels',
                moving,
                transitId: null,
                status: interfaceVariables.displayPortVessels_boolean,
                loading: interfaceVariables.displayPortVessels_boolean,
                drawTrack: false,
            };
        }

        if (vessel.loa_m < minimumLength) {
            return {
                group: 'otherVessels',
                moving,
                transitId: null,
                status: interfaceVariables.displayOtherVessels_boolean,
                loading: interfaceVariables.displayOtherVessels_boolean,
                drawTrack: false,
            };
        } else {
            //if this is a transitShips group
            if (transitShips?.length) {
                // if in it then it's otherTransit
                if (
                    transitShips.find((x) => {
                        return x.mmsi === vessel.mmsi;
                    })
                ) {
                    //if not then to the other vessel
                    return {
                        group: mode === 'search' ? 'otherTransits' : 'transitVessels',
                        moving,
                        transitId: null,
                        status: interfaceVariables.displayTransitVessels_boolean,
                        loading: interfaceVariables.displayTransitVessels_boolean,
                        drawTrack: false,
                    };
                }
                return {
                    group: 'otherVessels',
                    moving,
                    status: interfaceVariables.displayOtherVessels_boolean,
                    loading: interfaceVariables.displayOtherVessels_boolean,
                    drawTrack: false,
                };
            } else {
                return {
                    group: mode === 'search' ? 'otherTransits' : 'transitVessels',
                    moving,
                    transitId: null,
                    status: interfaceVariables.displayTransitVessels_boolean,
                    loading: interfaceVariables.displayTransitVessels_boolean,
                    drawTrack: false,
                };
            }
        }
    };

    Object.keys(sourceTracks).forEach((sourceTrack) => {
        const bestData = sourceTracks[sourceTrack].best;
        const data = sourceTracks[sourceTrack][bestData].data;
        const { group, transitId, status, drawTrack, moving } = getVesselGroup(
            mmsi,
            sourceTracks[sourceTrack].vessel,
            interfaceVariables,
            minimumLength,
            portShips,
            transitShips,
            data
        );

        tracks.push({
            uuid: getId(),

            mmsi: sourceTracks[sourceTrack].vessel.mmsi, // v2 map // same
            vessel_id: sourceTracks[sourceTrack].vessel.id,
            vessel: sourceTracks[sourceTrack].vessel,

            data: {
                events: [],
                offsets: [],
            },
            // static data
            //@ts-ignore
            //data: { ...trackData, offsets: tracks[track].offsets, [`${transit['best']}Id`]: trackId },
            // static options
            options: {
                group: group,
                moving: moving,
                display: status, //status
                loading: false,
                transitId: transitId,
                drawTrack: drawTrack,
                trackLoaded: false,

                rawAIS: switchs.rawAIS.value,
                centroid: switchs.centroid.value,
                smoothed: switchs.smoothed.value,

                rawAISLoaded: false,
                centroidLoaded: false,
                smoothedLoaded: false,

                vesselStatus: switchs.vesselStatus.value,
                systemTags: switchs.systemTags.value,
                routeTags: switchs.routeTags.value,
                debugTags: switchs.debugTags.value,
            },
        });
    });

    return tracks;
}

export function formatTooltips(toolTips: ToolTipType[], dateTimeFormat: dateTimeFormat): ToolTipType[] {
    return toolTips.map((x: ToolTipType) => {
        return { ...x, dataIndex: { ...x.dataIndex, time: dateTimeFormat.parseDateTime(x.dataIndex.time.isoDate) } };
    });
}

export function findBestData(
    data,
    afterTime?: DateTimeFormat | null,
    beforeTime?: DateTimeFormat | null,
    dataType?: string
) {
    const isWithinTimeRange = (itemTime, after, before) => {
        const timeStamp = itemTime.toTimestamp();
        const afterValid = after ? timeStamp >= after.toTimestamp() : true;
        const beforeValid = before ? timeStamp <= before.toTimestamp() : true;
        return afterValid && beforeValid;
    };

    const tryReturn = (sourceData, type, center = true) => {
        if (afterTime || beforeTime) {
            const validItems = sourceData.filter((item) =>
                //@ts-ignore
                isWithinTimeRange(item.valid_time_utc, afterTime, beforeTime)
            );

            if (validItems.length) return { center, data: validItems, type };
            else return null;
        }

        return { center, data: sourceData, type };
    };
    const rawAISresult = tryReturn(data?.rawAIS || [], 'rawAIS', false);
    if (dataType) {
        const result = tryReturn(data[dataType] || [], dataType, true);
        if (result) return { ...result, rawData: rawAISresult };
    } else {
        if (data?.smoothed?.length) {
            const result = tryReturn(data.smoothed || [], 'smoothed', true);

            if (result) return { ...result, rawData: rawAISresult };
        }
        if (data?.centroid?.length) {
            const result = tryReturn(data.centroid || [], 'centroid', true);
            if (result) return { ...result, rawData: rawAISresult };
        }

        if (rawAISresult) return { ...rawAISresult, rawData: rawAISresult };
    }

    // Fallback result
    return { center: false, data: data?.rawAIS || [], type: 'rawAIS', rawData: rawAISresult };
}

export function findBestPosition(data, type, rawData, currentTime: DateTimeFormat) {
    const position = data
        .filter(
            //@ts-ignore
            (item) => item.valid_time_utc.toTimestamp() <= currentTime?.toTimestamp()
        )
        .sort((a, b) => b.valid_time_utc.toTimestamp() - a.valid_time_utc.toTimestamp())[0];

    const rawPosition = rawData
        .filter(
            //@ts-ignore
            (item) => item.valid_time_utc.toTimestamp() <= currentTime?.toTimestamp()
        )
        .sort((a, b) => b.valid_time_utc.toTimestamp() - a.valid_time_utc.toTimestamp())[0];
    if (position?.valid_time_utc.toTimestamp() >= rawPosition?.valid_time_utc.toTimestamp()) {
        return { position, center: type === 'rawAIS' ? false : true };
    }
    return { position: rawPosition, center: false };
}

export function formatSavedFilter(dateTimeFormat: DateTimeFormat, filter: FilterObjectType) {
    //@ts-ignore
    if (filter.dateRange != undefined) {
        //@ts-ignore
        setKey(filter, 'date.dateRange', filter.dateRange || 'lastDays');
    }
    //@ts-ignore
    if (filter.lastDays != undefined) {
        //@ts-ignore
        setKey(filter, 'date.lastDays', Math.abs(filter.lastDays));
    }

    if (
        ((filter.date.lastDays === '' || filter.date.lastDays === 0) && filter.date.dateRange === '') ||
        filter.date.dateRange === 'activeDays'
    ) {
        setKey(
            filter,
            'date.start_utc.op',
            filter.date.start_utc?.op === null ? null : dateTimeFormat.parseDateTime(filter.date.start_utc?.op?.date)
        );
        setKey(
            filter,
            'date.end_utc.op',
            filter.date.end_utc?.op === null ? null : dateTimeFormat.parseDateTime(filter.date.end_utc?.op?.date)
        );
    } else {
        const { startDate, endDate } = dateTimeFormat
            .clone()

            .getDateFromDays(
                filter.date.lastDays === 0 && filter.date.dateRange === '' ? 'activeToday' : filter.date.dateRange,
                //@ts-ignore
                filter.date.lastDays
            );

        setKey(filter, 'date.end_utc.op', endDate);
        setKey(filter, 'date.start_utc.op', startDate);
    }
    //@ts-ignore
    setKey(filter, 'date.lastDays', filter.date.lastDays === '' ? '' : Math.abs(filter.date.lastDays));
    setKey(
        filter,
        'date.dateRange',
        filter.date.lastDays === 0 && filter.date.dateRange === '' ? 'activeToday' : filter.date.dateRange
    );
    return filter;
}

export function replaceOptions(transits: TransitType[], options: TransitOptionType[]): TransitType[] {
    return transits.map((transit: TransitType) => {
        const option = options.find((x) => {
            //@ts-ignore
            return x.id === transit.id;
        });
        if (!option) {
            return transit;
        }
        return {
            ...transit,
            options: {
                ...transit.options,
                display: option.display,
                highlight: option.highlight,
                inspect: option.inspect,
                outline: option.inspect ? false : option.outline,
                oldOutline: option.oldOutline,
                oldDrawTrack: option.oldDrawTrack,
                drawTrack: option.inspect ? false : option.drawTrack,
                drawSweptPath: option.drawSweptPath,
                select: option.select,
            },
        };
    });
}

export function formatTransits(dateTimeFormat: DateTimeFormat, tracks, switchs: SwitchType) {
    const transits: TransitType[] = [];
    const transitsData: TransitsDataType = {};

    Object.keys(tracks).forEach((track) => {
        tracks[track].transits.map((transit: any, index: number) => {
            const { trackState, trackData, trackId } = getBestData(
                dateTimeFormat,
                transit['best'],
                transit.id,
                //@ts-ignore
                dateTimeFormat.parseDateTime(transit.start_utc, 'transit start time'),
                //@ts-ignore
                dateTimeFormat.parseDateTime(transit.end_utc, 'transit end time'),
                tracks[track]
            );

            const uuid = getId();
            //@ts-ignore
            transitsData[uuid] = { ...trackData, offsets: tracks[track].offsets, [`${transit['best']}Id`]: trackId };

            transits.push({
                uuid: uuid,

                id: transit.id, //v2 map // transit_id // string
                mmsi: transit.mmsi, // v2 map // same
                transit_tags: transit.transit_tags, // list of tags used in grid // v2 map // need to map with tags to convert uuid to object

                best: transit['best'], // used to know what is the best data inside the data object // v2 map // detect from the links and find the best
                //@ts-ignore
                start_utc: dateTimeFormat.parseDateTime(transit.start_utc), // start time UTC // v2 map // start_time_utc
                //@ts-ignore
                end_utc: dateTimeFormat.parseDateTime(transit.end_utc), // end time UTC // v2 map // end_time_utc
                //@ts-ignore
                updated_at: dateTimeFormat.parseDateTime(transit.updated_at), // v2 map // last_updated_time_utc
                draft_m: transit.draft_m,

                transit_analysis: transit.transit_analysis, // v2 map // MISSING
                visit_id: transit.visit_id, // v2 map // MISSING
                status: transit.status, // v2 map //  transit_state
                events: transit.events, // v2 map // MISSING
                comments: transit.comments, // v2 map // MISSING
                environmental: Boolean(transit.environmental), // v2 map // environmental
                analysis: Boolean(transit.analysis), // v2 map // analysis

                // vessel data
                vessel: tracks[track].vessel, //or vessel_id // v2 map // vessel
                vessel_id: tracks[track].vessel_id, // v2 map // vessel // MISSING

                // static data
                //@ts-ignore
                data: {},
                // static options
                options: {
                    display: transit['type'] !== 'draft',
                    expand: false,
                    highlight: false,
                    select: transit['type'] !== 'draft',
                    inspect: false,

                    drawSweptPath: switchs.sweptPath.value,
                    sweptPathLoaded: false,

                    drawTrackLoaded: trackState,
                    drawTrack: switchs.vesselTracks.value,
                    oldDrawTrack: switchs.vesselTracks.value,

                    outline: switchs.vesselOutline.value,
                    oldOutline: switchs.vesselOutline.value,
                },

                // data: {},
                // coordinates: tracks[track][transit['best']].coordinates,
            });
        });
    });

    return { transits, transitsData };
}

function getBestData(
    dateTimeFormat: DateTimeFormat,
    bestData: string,
    transitId: number,
    startTime: DateTimeFormat,
    endTime: DateTimeFormat,
    track: TrackType
) {
    // Ensure the track object and its properties exist to avoid runtime errors
    if (!track || !track[bestData] || !track[bestData].coordinates || !track[bestData].data) {
        return { trackState: false, trackId: null, trackData: { [bestData]: [] } };
    }

    // Safely access the coordinates at the specified index
    const sigmants = track[bestData].coordinates;
    const coordinates: number[][] = [];
    sigmants.forEach((sigmant) => {
        sigmant.forEach((pointIndex) => {
            const point = track[bestData].data[pointIndex];
            if (point && point.transit_id === transitId) {
                if (dateTimeFormat.parseDateTime(point.valid_time_utc).isBetween(startTime.date, endTime.date, true )) {
                    coordinates.push(point);
                }
            }
        });
    });

    if (!coordinates || !Array.isArray(coordinates)) {
        window?.log(`ERROR : Transit ${transitId}has no best data `, 'color: red');
        return { trackState: false, trackData: { [bestData]: [] }, trackId: null };
    }

    return {
        trackState: coordinates.length > 0,
        trackId: getId(),
        trackData: { [bestData]: coordinates },
    };
}

export function formatEvents(events: EventType[]): EventType[] {
    return events
        .filter(
            (event: EventType) =>
                [
                    'enterSwing',
                    'enterChannel',
                    'enterPilotBoardingGround',
                    'exitPilotBoardingGround',
                    'atBerth',
                    'enterBerth',
                    'exitBerth',
                    'enterAnchorage',
                    'exitAnchorage',
                ].includes(event.type) && event.description != null
        )
        .filter((event, index, data) => {
            if ((index == 0 || data.length - 1 == index) && event.description.indexOf('`') >= 0) {
                return true;
            }
            return false;
        })
        .map((event) => {
            return {
                ...event,
                description: formatEventText(event?.description),
                valid_time_utc: event.valid_time_utc,
            };
        });
}

export function formatEventText(text: string): string {
    //@ts-ignore
    return text?.match(new RegExp('`(.*)`'))[1] || '';
}

export function getEnviroDisplayDataAvrages(
    transit,
    environmentalSources,
    enviroDisplayOrders,
    enviroDisplay,
    isUsed = true
) {
    const isUsedItem = (transit, id) => {
        const transitAnalysis = transit.transit_analysis.filter((item) => {
            return item.port_url_id == id;
        });

        return transitAnalysis.length != 0;
    };

    const isUsedValue = (transit, type, key, source) => {
        const transitAnalysis = transit.transit_analysis.find((item) => {
            return item.key == key && item.type == type && item.port_url_id == source;
        });
        if (transitAnalysis == undefined) {
            return false;
        }
        if (transitAnalysis.value == null) {
            return false;
        }
        return true;
    };

    const getFormattedTransitValue = (transit, type, key, source, options) => {
        if (!transit) {
            return undefined;
        }

        const transitAnalysis = transit.transit_analysis.find(
            (item) => item.key === key && item.type === type && item.port_url_id === source
        );

        if (!transitAnalysis) {
            return undefined;
        }

        if (transitAnalysis.value == null) {
            return null;
        }

        const value = transitAnalysis.value * options.database2display_unit_factor;
        const decimals = options.decimal || 0;
        const factor = Math.pow(10, decimals);
        const roundedValue = Math.round(value * factor) / factor;

        return roundedValue % 1 === 0 ? roundedValue.toFixed(decimals) : roundedValue;
    };

    return enviroDisplayOrders.map((enviroDisplayOrder) => {
        return {
            key: enviroDisplayOrder.key,
            instruments: environmentalSources
                .filter((environmentalSource) => {
                    if (!isUsed) {
                        return environmentalSource.environmental_type == enviroDisplayOrder.key;
                    }
                    return (
                        environmentalSource.environmental_type == enviroDisplayOrder.key &&
                        isUsedItem(transit, environmentalSource.id)
                    );
                })
                .sort((a, b) => a.order - b.order)
                .map((environmentalSource) => {
                    const values: any = {
                        id: environmentalSource.id,
                        description: environmentalSource.description,
                        order: environmentalSource.order,
                        values: enviroDisplayOrder.list
                            .filter((enviroDisplayOrderkey) => {
                                return isUsedValue(
                                    transit,
                                    enviroDisplayOrder.key,
                                    enviroDisplayOrderkey,
                                    environmentalSource.id
                                );
                            })
                            .map((enviroDisplayOrderkey, enviroDisplayOrderindex) => {
                                return {
                                    index: enviroDisplayOrderindex,
                                    name: enviroDisplay[enviroDisplayOrder.key][enviroDisplayOrderkey].name,
                                    key: enviroDisplayOrderkey,
                                    value: getFormattedTransitValue(
                                        transit,
                                        enviroDisplayOrder.key,
                                        enviroDisplayOrderkey,
                                        environmentalSource.id,
                                        enviroDisplay[enviroDisplayOrder.key][enviroDisplayOrderkey]
                                    ),
                                    unit: enviroDisplay[enviroDisplayOrder.key][enviroDisplayOrderkey].unit,
                                };
                            }),
                    };

                    return values;
                }),
        };
    });
}

export function unifiedSelectedPlan(pilotagePlans) {
    pilotagePlans = pilotagePlans ? pilotagePlans : { plans: [] };

    pilotagePlans?.plans.map((pilotagePlan) => {
        pilotagePlan.group = [
            ...(pilotagePlan?.noGoAreas || []),
            ...(pilotagePlan?.safetyMargins || []),
            ...(pilotagePlan?.markerPoints || []),
            pilotagePlan.route,
            pilotagePlan.XTD,
        ];
        return pilotagePlan;
    });
}
